import AvatarV2 from "@/components/Avatar/AvatarV2";
import useCGPStore from "@/hooks/useCGPStore";
import { useMyActivityV3 } from "@/hooks/useMyActivityV3";
import { processedActivities } from "@/types/my-activities";
import { PAGE_URL } from "@/utils/constants";
import { 
  getProfileUrl,
  shouldShowContextReference,
  shouldShowContextReferenceSkill,
  getCustomReferenceType,
  shouldRenderActionBtn,
  shouldRenderActionBtnConnectActReq,
  shouldRenderConnectionReqSingleBtn
} from "@/utils/activityUtils";
import dayjs from "dayjs";
import { capitalize, lowerCase } from "lodash";
import Link from "next/link";
import { memo, useMemo, useState } from "react";
import ActivityActionButtons from "./ActivityActionButtons";

const ActivityItemV3 = ({
  context,
  contextProfile,
  createdAt,
  description,
  contextReference,
  notes,
  onMeasured,
  actionPayload,
  additionalInfo,
}: processedActivities) => {
  const [isLoadingProcess, isLoadingProcessSet] = useState<
    "ACCEPTED" | "DECLINED" | null
  >(null);

  const { activityCGP } = useCGPStore();

  const { activityActionsMethods } = useMyActivityV3({
    actionLoaderSet: isLoadingProcessSet,
  });


  const shouldRenderContextReference = useMemo(() => shouldShowContextReference(context, contextReference), [context, contextReference]);
  const shouldRenderContextReferenceSkill = useMemo(() => shouldShowContextReferenceSkill(context, contextReference), [context, contextReference]);

  const customReferenceType = useMemo(() => getCustomReferenceType(contextReference), [contextReference]);

  const showActionBtn = useMemo(() => shouldRenderActionBtn(context, additionalInfo), [context, additionalInfo]);

  const showActionBtnConnectActReq = useMemo(() => shouldRenderActionBtnConnectActReq(context, description, additionalInfo), [context, description, additionalInfo]);

  const showConnectionReqSingleBtn = useMemo(() => shouldRenderConnectionReqSingleBtn(context, additionalInfo), [context, additionalInfo]);


  const urlLinkAvatarContextProfile = useMemo(() => getProfileUrl(contextProfile?.type || '', contextProfile?.id || ''), [contextProfile?.type, contextProfile?.id]);
  const urlLinkAvatarContextReference = useMemo(() => getProfileUrl(contextReference?.type || '', contextReference?.id || ''), [contextReference?.type, contextReference?.id]);

  return (
    <div className="bg-card py-4 px-6 flex flex-col" role="article" aria-label="Activity item">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Link href={urlLinkAvatarContextProfile}>
            {!contextProfile?.isHR && (
              <AvatarV2
                id={contextProfile?.id}
                src={contextProfile?.avatarUrl}
                alt={contextProfile?.name || "User Avatar"}
                onMeasured={onMeasured}
                points={
                  contextProfile?.type === "USER"
                    ? contextProfile?.points
                    : undefined
                }
                square={contextProfile?.type !== "USER"}
                CGP={
                  !contextProfile?.isHR
                    ? activityCGP?.find(
                        (x) =>
                          x?.id === (contextProfile?.id || contextReference?.id)
                      )?.CGPercent || 0
                    : undefined
                }
              />
            )}
          </Link>
          <div className="flex flex-col gap-1">
            {/* <div>{contextProfile?.type}</div> */}
            <div
              className="text-sm"
              dangerouslySetInnerHTML={{ __html: description }}
              style={{ wordBreak: "break-word" }}
            />
            {createdAt && (
              <time
                className="text-xs"
                dateTime={new Date(createdAt).toISOString()}
                aria-label={`Created at ${dayjs(createdAt).format("MMMM DD, YYYY @hh:mma")}`}
              >
                {dayjs(createdAt).format("MMMM DD, YYYY @hh:mma")}
              </time>
            )}
          </div>
        </div>
        {showConnectionReqSingleBtn && (
          <div className="w-full lg:w-auto">
            <ActivityActionButtons
              mode="confirm"
              blockOnMobile
              confirmLabel="Yes, Connect"
              loadingPurpose={isLoadingProcess}
              onConfirm={() =>
                activityActionsMethods?.[
                  context as keyof typeof activityActionsMethods
                ]?.({ ...actionPayload, purpose: "ACCEPTED" })
              }
            />
          </div>
        )}
      </div>
      {((showActionBtn && notes) ||
        (shouldRenderContextReference && notes)) && (
        <div className="bg-card pb-2 mt-2">
          <div className="p-4 bg-[#262626] w-full">
            <p
              className="text-sm"
              dangerouslySetInnerHTML={{ __html: notes || "" }}
            />
          </div>
        </div>
      )}
      {(showActionBtn || showActionBtnConnectActReq) && (
        <div className="mt-2">
          <ActivityActionButtons
            mode="dual"
            loadingPurpose={isLoadingProcess}
            onAccept={() =>
              activityActionsMethods?.[
                context as keyof typeof activityActionsMethods
              ]?.({ ...actionPayload, purpose: "ACCEPTED" })
            }
            onDecline={() =>
              activityActionsMethods?.[
                context as keyof typeof activityActionsMethods
              ]?.({ ...actionPayload, purpose: "DECLINED" })
            }
          />
        </div>
      )}
      {shouldRenderContextReference && (
        <>
          <hr className="border-black mt-2 mb-4" />
          <div className="flex justify-between gap-3 flex-col md:flex-row">
            <div className="flex gap-4 items-center">
              <Link href={urlLinkAvatarContextReference}>
                <AvatarV2
                  id={contextReference?.id || ""}
                  src={contextReference?.avatarUrl || ""}
                  alt={contextReference?.name || "User Avatar"}
                  onMeasured={onMeasured}
                  points={
                    contextReference?.type === "USER"
                      ? contextReference?.points
                      : undefined
                  }
                  square={contextReference?.type !== "USER"}
                  CGP={
                    !contextReference?.isHR
                      ? activityCGP?.find(
                          (x) =>
                            x?.id ===
                            (contextReference?.id || contextReference?.id)
                        )?.CGPercent || 0
                      : undefined
                  }
                />
              </Link>
              <div>
                {contextReference?.type === "USER" && (
                  <div className="flex flex-col gap-1">
                    <p className="text-xs text-[#D9D9D9]">
                      {contextReference?.jobTitle}
                    </p>
                    <h4 className="text-sm font-bold">
                      {contextReference?.name}
                    </h4>
                    <p className="text-xs text-[#D9D9D9]">
                      {contextReference?.lookingForType}
                    </p>
                  </div>
                )}
                {contextReference?.type !== "USER" && (
                  <div className="flex flex-col">
                    <h4 className="text-sm font-bold">
                      {contextReference?.name}
                    </h4>
                    <p className="text-xs text-[#D9D9D9]">
                      {customReferenceType}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <ActivityActionButtons
              mode="dual"
              loadingPurpose={isLoadingProcess}
              onAccept={() =>
                activityActionsMethods?.[
                  context as keyof typeof activityActionsMethods
                ]?.({ ...actionPayload, purpose: "ACCEPTED" })
              }
              onDecline={() =>
                activityActionsMethods?.[
                  context as keyof typeof activityActionsMethods
                ]?.({ ...actionPayload, purpose: "DECLINED" })
              }
            />
          </div>
        </>
      )}
      {shouldRenderContextReferenceSkill && (
        <>
          <hr className="border-black mt-2 mb-4" />
          <div>
            <p className="font-bold">{contextReference?.name}</p>
            <div
              style={{
                borderLeft: `4px solid ${contextReference?.color}`,
              }}
              className="pl-2 mt-1 py-1"
            >
              <p>
                Department:{" "}
                <span className="font-bold text-sm">
                  {contextReference?.department || " "}
                </span>
                {contextReference?.type === "SKILL" && (
                  <>
                    , Skill Ladder:{" "}
                    <span className="font-bold">
                      {contextReference?.skillName || " "}
                    </span>
                    , Skill Level:{" "}
                    <span className="font-bold">
                      {contextReference?.skillLevel || " "}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="mt-4">
              <ActivityActionButtons
                mode="dual"
                loadingPurpose={isLoadingProcess}
                onAccept={() =>
                  activityActionsMethods?.[
                    context as keyof typeof activityActionsMethods
                  ]?.({ ...actionPayload, purpose: "ACCEPTED" })
                }
                onDecline={() =>
                  activityActionsMethods?.[
                    context as keyof typeof activityActionsMethods
                  ]?.({ ...actionPayload, purpose: "DECLINED" })
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(ActivityItemV3);
